import type { ReactNode } from 'react';
import { Filter, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

interface ColumnFilterProps {
  /** 列名。ボタンの読み上げに使う */
  label: string;
  /** 絞り込みが効いているか。効いていればアイコンを強調する */
  active: boolean;
  onClear: () => void;
  children: ReactNode;
}

/**
 * 列ヘッダーに置く絞り込みボタン（FKV3-13002）
 *
 * どの列で絞っているかがヘッダーを見れば分かるよう、効いている列のアイコンを色で示す。
 */
export function ColumnFilter({ label, active, onClear, children }: ColumnFilterProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label}で絞り込む${active ? '（絞り込み中）' : ''}`}
          className={`ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded transition hover:bg-gray-200 ${
            active ? 'bg-blue-100 text-blue-700' : 'text-gray-400'
          }`}
        >
          <Filter className="h-3 w-3" fill={active ? 'currentColor' : 'none'} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-xs font-medium">{label}で絞り込む</span>
          {active && (
            <Button type="button" variant="ghost" size="sm" onClick={onClear} className="h-6 px-2">
              <X className="mr-1 h-3 w-3" />
              解除
            </Button>
          )}
        </div>
        <div className="p-3">{children}</div>
      </PopoverContent>
    </Popover>
  );
}

interface TextColumnFilterProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

/** テキストで部分一致させる列の絞り込み */
export function TextColumnFilter({ label, value, onChange }: TextColumnFilterProps) {
  return (
    <ColumnFilter label={label} active={value.trim().length > 0} onClear={() => onChange('')}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`${label}を含む`}
        autoFocus
        className="h-8"
      />
    </ColumnFilter>
  );
}

/** 選択肢1件 */
export interface FilterOption {
  id: string;
  name: string;
  count: number;
}

interface SelectColumnFilterProps {
  label: string;
  options: FilterOption[];
  /** 選択中の値。空なら絞り込みなし（全件） */
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}

/**
 * 選択肢から選ぶ列の絞り込み（タグ種別の列で使う）。
 *
 * 何も選んでいない状態を「絞り込みなし」とする。選んだものだけを表示する。
 */
export function SelectColumnFilter({
  label,
  options,
  selected,
  onChange,
}: SelectColumnFilterProps) {
  const toggle = (id: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    onChange(next);
  };

  return (
    <ColumnFilter label={label} active={selected.size > 0} onClear={() => onChange(new Set())}>
      <div className="flex flex-col gap-1">
        <div className="mb-1 flex gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 flex-1 px-2 text-xs"
            onClick={() => onChange(new Set(options.map((o) => o.id)))}
          >
            すべて選択
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 flex-1 px-2 text-xs"
            onClick={() => onChange(new Set())}
          >
            すべて解除
          </Button>
        </div>
        <div className="max-h-56 overflow-y-auto">
          {options.map((option) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-gray-50"
            >
              <Checkbox
                checked={selected.has(option.id)}
                onCheckedChange={(checked) => toggle(option.id, checked === true)}
              />
              <span className="flex-1 truncate">{option.name}</span>
              <span className="tabular-nums text-gray-400">{option.count}</span>
            </label>
          ))}
        </div>
      </div>
    </ColumnFilter>
  );
}
